import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import DeleteIcon from "@mui/icons-material/Delete";
import PersonAddIcon from "@mui/icons-material/PersonAdd";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Avatar,
  Box,
  Button,
  Card,
  CardActions,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  InputAdornment,
  Paper,
  Snackbar,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { useCallback, useEffect, useMemo, useState } from "react";
import PageHeader from "../components/PageHeader";
import SectionCard from "../components/SectionCard";
import { api, describeError } from "../services/api";
import type {
  AccountManager,
  AccountManagerStatus,
  InviteAccountManagerResponse,
} from "../types/account-manager";

const STATUS_META: Record<
  AccountManagerStatus,
  { label: string; color: "success" | "warning" | "default"; hint: string }
> = {
  active: {
    label: "Verified",
    color: "success",
    hint: "Completed the invite link and can receive inquiries",
  },
  pending: {
    label: "Pending invite",
    color: "warning",
    hint: "Waiting for the manager to open the one-time invite link",
  },
  disabled: {
    label: "Disabled",
    color: "default",
    hint: "Excluded from round-robin until re-enabled",
  },
};

function displayName(m: AccountManager): string {
  const name = [m.firstName, m.lastName].filter(Boolean).join(" ").trim();
  if (name) return name;
  if (m.username) return `@${m.username}`;
  return `User ${m.baleUserId}`;
}

function initials(m: AccountManager): string {
  if (m.firstName) return m.firstName.slice(0, 1).toUpperCase();
  if (m.username) return m.username.slice(0, 1).toUpperCase();
  return String(m.baleUserId).slice(0, 1);
}

export default function AccountManagersPage() {
  const [managers, setManagers] = useState<AccountManager[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);

  // Invite form
  const [baleUserIdInput, setBaleUserIdInput] = useState("");
  const [inviting, setInviting] = useState(false);
  const [inviteResult, setInviteResult] =
    useState<InviteAccountManagerResponse | null>(null);

  // Delete confirm
  const [deleteTarget, setDeleteTarget] = useState<AccountManager | null>(null);
  /** Inquiries routed in the last 30 days, keyed by Bale user id. */
  const [loadByManagerId, setLoadByManagerId] = useState<Map<number, number>>(new Map());

  const load = useCallback(async (initial = false) => {
    if (initial) setLoading(true);
    setError(null);
    try {
      const [items, summary] = await Promise.all([
        api.getAccountManagers(),
        api.getAnalytics(30).catch(() => null),
      ]);
      setManagers(items);
      if (summary) {
        const map = new Map<number, number>();
        for (const row of summary.managerLoad) {
          if (row.managerId) map.set(row.managerId, row.count);
        }
        setLoadByManagerId(map);
      }
    } catch (err) {
      setError(describeError(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    void api.getAccountManagers().then((items) => {
      if (!cancelled) setManagers(items);
    }).catch((err) => {
      if (!cancelled) {
        setError(describeError(err));
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    void api.getAnalytics(30).then((summary) => {
      if (!cancelled && summary) {
        const map = new Map<number, number>();
        for (const row of summary.managerLoad) {
          if (row.managerId) map.set(row.managerId, row.count);
        }
        setLoadByManagerId(map);
      }
    }).catch((err) => {
      if (!cancelled) {
        setError(describeError(err));
      }
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [load]);

  const sorted = useMemo(() => {
    const order: Record<AccountManagerStatus, number> = {
      active: 0,
      pending: 1,
      disabled: 2,
    };
    return [...managers].sort((a, b) => {
      const byStatus = order[a.status] - order[b.status];
      if (byStatus !== 0) return byStatus;
      return a.baleUserId - b.baleUserId;
    });
  }, [managers]);

  const counts = useMemo(() => {
    return {
      active: managers.filter((m) => m.status === "active").length,
      pending: managers.filter((m) => m.status === "pending").length,
      disabled: managers.filter((m) => m.status === "disabled").length,
    };
  }, [managers]);

  const handleInvite = async () => {
    const id = Number(baleUserIdInput.trim());
    if (!Number.isInteger(id) || id <= 0) {
      setError("Enter a positive Bale user id (numeric).");
      return;
    }
    setInviting(true);
    setError(null);
    setInviteResult(null);
    try {
      const result = await api.inviteAccountManager(id);
      setInviteResult(result);
      setBaleUserIdInput("");
      setSuccess("Invite created — share the link with the manager");
      await load();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setInviting(false);
    }
  };

  const copyLink = async (link: string) => {
    try {
      await navigator.clipboard.writeText(link);
      setSuccess("Invite link copied");
    } catch {
      setError("Could not copy to clipboard");
    }
  };

  const setStatus = async (m: AccountManager, status: "active" | "disabled") => {
    setBusyId(m.id);
    setError(null);
    try {
      await api.updateAccountManager(m.id, { status });
      setSuccess(status === "disabled" ? "Manager disabled" : "Manager re-enabled");
      await load();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusyId(null);
    }
  };

  const reinvite = async (m: AccountManager) => {
    setBusyId(m.id);
    setError(null);
    setInviteResult(null);
    try {
      const result = await api.inviteAccountManager(m.baleUserId);
      setInviteResult(result);
      setSuccess("New invite link issued");
      await load();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusyId(null);
    }
  };

  const confirmDelete = async () => {
    if (!deleteTarget) return;
    setBusyId(deleteTarget.id);
    setError(null);
    try {
      await api.deleteAccountManager(deleteTarget.id);
      setSuccess("Account manager removed");
      setDeleteTarget(null);
      await load();
    } catch (err) {
      setError(describeError(err));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Stack spacing={3} className="stagger">
      <PageHeader
        title="Account managers"
        description="Invite by Bale user id. Managers verify once via a one-time deep link, then join round-robin with a username contact button."
        actions={
          <Button
            startIcon={<RefreshIcon />}
            onClick={() => void load()}
            disabled={loading}
            variant="outlined"
          >
            Refresh
          </Button>
        }
      />

      {error && (
        <Alert severity="error" onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Summary chips */}
      <Stack direction="row" spacing={1} useFlexGap sx={{ flexWrap: "wrap" }}>
        <Chip label={`${counts.active} verified`} color="success" variant="outlined" />
        <Chip label={`${counts.pending} pending`} color="warning" variant="outlined" />
        <Chip label={`${counts.disabled} disabled`} variant="outlined" />
      </Stack>

      {/* Invite panel */}
      <SectionCard
        title="Invite an account manager"
        description="Enter the manager's numeric Bale user id. You get a one-time link to send them; when they open it, their profile is completed and they can receive inquiries."
      >
        <Stack spacing={2}>
          <Stack
            direction={{ xs: "column", sm: "row" }}
            spacing={1.5}
            sx={{ alignItems: { sm: "flex-start" } }}
          >
            <TextField
              label="Bale user id"
              value={baleUserIdInput}
              onChange={(e) => setBaleUserIdInput(e.target.value.replace(/\D/g, ""))}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  void handleInvite();
                }
              }}
              inputMode="numeric"
              size="small"
              sx={{ minWidth: 220 }}
              disabled={inviting}
              helperText="Digits only, e.g. 758752736"
            />
            <Button
              variant="contained"
              startIcon={inviting ? <CircularProgress size={16} color="inherit" /> : <PersonAddIcon />}
              onClick={() => void handleInvite()}
              disabled={inviting || !baleUserIdInput.trim()}
            >
              {inviting ? "Creating…" : "Generate invite"}
            </Button>
          </Stack>

          {inviteResult && (
            <Alert
              severity="success"
              onClose={() => setInviteResult(null)}
              sx={{ alignItems: "flex-start" }}
            >
              <Typography variant="subtitle2" gutterBottom>
                Invite ready for user {inviteResult.manager.baleUserId}
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Send this link to the manager. It works only for that Bale account
                and only once.
              </Typography>
              <TextField
                value={inviteResult.inviteLink}
                fullWidth
                size="small"
                slotProps={{
                  input: {
                    readOnly: true,
                    endAdornment: (
                      <InputAdornment position="end">
                        <Tooltip title="Copy link">
                          <IconButton
                            edge="end"
                            onClick={() => void copyLink(inviteResult.inviteLink)}
                            size="small"
                          >
                            <ContentCopyIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </InputAdornment>
                    ),
                  }
                }}
              />
            </Alert>
          )}
        </Stack>
      </SectionCard>

      {/* Manager cards */}
      <Box>
        <Typography variant="h6" gutterBottom sx={{ mb: 2 }}>
          Current managers
        </Typography>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : sorted.length === 0 ? (
          <Paper sx={{ p: 3 }}>
            <Typography variant="body2" color="text.secondary">
              No account managers yet. Invite someone above — until at least one is
              verified, inquiries will fall back to the &quot;no manager&quot; message.
            </Typography>
          </Paper>
        ) : (
          <Box
            sx={{
              display: "grid",
              gap: 2,
              gridTemplateColumns: {
                xs: "1fr",
                sm: "repeat(2, 1fr)",
                lg: "repeat(3, 1fr)",
              },
            }}
          >
            {sorted.map((m) => {
              const meta = STATUS_META[m.status];
              const busy = busyId === m.id;
              return (
                <Card key={m.id} variant="outlined" sx={{ display: "flex", flexDirection: "column" }}>
                  <CardContent sx={{ flexGrow: 1 }}>
                    <Stack direction="row" spacing={2} sx={{ alignItems: "flex-start" }}>
                      <Avatar
                        src={m.profilePicture || ""}
                        sx={{
                          width: 85,
                          height: 85,
                          bgcolor:
                            m.status === "active"
                              ? "success.main"
                              : m.status === "pending"
                                ? "warning.main"
                                : "action.disabledBackground",
                          color:
                            m.status === "disabled" ? "text.secondary" : "common.white",
                        }}
                      >
                        {!m.profilePicture && initials(m) }
                      </Avatar>
                      <Box sx={{ minWidth: 0, flexGrow: 1 }}>
                        <Typography variant="subtitle1" noWrap title={displayName(m)}>
                          {displayName(m)}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" noWrap>
                          {m.username ? `@${m.username}` : "Username pending"}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{ fontVariantNumeric: "tabular-nums" }}
                        >
                          ID {m.baleUserId}
                        </Typography>
                      </Box>
                      <Tooltip title={meta.hint}>
                        <Chip size="small" label={meta.label} color={meta.color} />
                      </Tooltip>
                    </Stack>

                    <Stack spacing={0.5} sx={{ mt: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Invited {new Date(m.invitedAt).toLocaleString()}
                      </Typography>
                      {m.verifiedAt && (
                        <Typography variant="caption" color="text.secondary">
                          Verified {new Date(m.verifiedAt).toLocaleString()}
                        </Typography>
                      )}
                      <Typography
                        variant="body2"
                        sx={{ mt: 0.5, fontVariantNumeric: "tabular-nums" }}
                      >
                        {(loadByManagerId.get(m.baleUserId) ?? 0).toLocaleString("en-US")}{" "}
                        <Typography component="span" variant="caption" color="text.secondary">
                          inquiries · last 30 days
                        </Typography>
                      </Typography>
                    </Stack>
                  </CardContent>

                  <CardActions sx={{ px: 2, pb: 2, pt: 0, flexWrap: "wrap", gap: 0.5 }}>
                    {m.status === "active" && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => void setStatus(m, "disabled")}
                      >
                        Disable
                      </Button>
                    )}
                    {m.status === "disabled" && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => void setStatus(m, "active")}
                      >
                        Enable
                      </Button>
                    )}
                    {(m.status === "pending" || m.status === "disabled") && (
                      <Button
                        size="small"
                        disabled={busy}
                        onClick={() => void reinvite(m)}
                      >
                        Re-invite
                      </Button>
                    )}
                    <Box sx={{ flexGrow: 1 }} />
                    <Tooltip title="Remove">
                      <span>
                        <IconButton
                          size="small"
                          color="error"
                          disabled={busy}
                          onClick={() => setDeleteTarget(m)}
                        >
                          {busy ? (
                            <CircularProgress size={16} />
                          ) : (
                            <DeleteIcon fontSize="small" />
                          )}
                        </IconButton>
                      </span>
                    </Tooltip>
                  </CardActions>
                </Card>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Delete dialog */}
      <Dialog
        open={!!deleteTarget}
        onClose={() => !busyId && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Remove account manager</DialogTitle>
        <DialogContent>
          <Typography>
            Remove{" "}
            <strong>
              {deleteTarget ? displayName(deleteTarget) : ""}
            </strong>{" "}
            (ID {deleteTarget?.baleUserId})? They will no longer receive inquiries.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={!!busyId}>
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void confirmDelete()}
            disabled={!!busyId}
          >
            {busyId ? <CircularProgress size={20} /> : "Remove"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={!!success}
        autoHideDuration={4000}
        onClose={() => setSuccess(null)}
        message={success}
      />
    </Stack>
  );
}
